import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { CartItem, CartState, CartReviewData, DiscountInfo, ShippingTier } from '../types/cart';
import { trackDiscountApplied, trackDiscountFailed } from '../utils/tracking';
import { dispatchCartCount } from '../utils/cartBadge';
import { getCSRFToken } from '../utils/csrf';
import { route } from '../utils/route';
import { getTabId } from './useCartSync';

// Debounce time for server sync (ms)
const SYNC_DEBOUNCE_MS = 800;

export function useCart(initialData: CartReviewData, onRefresh?: () => Promise<CartReviewData>) {
  const [state, setState] = useState<CartState>(() => ({
    items: initialData.items,
    minOrder: initialData.minOrder,
    discountCode: initialData.discountCode,
    discountAmount: initialData.discountAmount ?? 0,
    discountPercentage: initialData.discountPercentage ?? null,
    discountableItems: initialData.discountableItems ?? [],
    discountError: null,
    deliveryPrice: initialData.deliveryPrice,
    cartShare: initialData.cartShare,
    isLoading: false,
    updatingItemId: null,
  }));

  const [discountInfo, setDiscountInfo] = useState<DiscountInfo | null>(initialData.discountInfo);

  // Track pending quantity changes for debounced sync
  const pendingChangesRef = useRef<Map<string, number>>(new Map());
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncingItemsRef = useRef<Set<string>>(new Set());

  // Preview state for the two-step discount application flow
  const [previewData, setPreviewData] = useState<{
    code: string;
    discount: number;
    discountPercentage: number;
    discountableItems: string[];
    discountInfo: DiscountInfo;
  } | null>(null);

  const shippingTiers = initialData.shippingTiers ?? null;
  const currentShippingTier = initialData.currentShippingTier ?? null;
  const nextShippingTier = initialData.nextShippingTier ?? null;

  // Sync with server data when initialData changes (from onRefresh)
  useEffect(() => {
    setState(prev => ({
      ...prev,
      items: initialData.items,
      minOrder: initialData.minOrder,
      discountCode: initialData.discountCode,
      discountAmount: initialData.discountAmount ?? 0,
      // Use server-provided discountPercentage and discountableItems if available
      // Otherwise preserve existing values if discount code matches
      discountPercentage: initialData.discountPercentage ?? (prev.discountCode === initialData.discountCode ? prev.discountPercentage : null),
      discountableItems: initialData.discountableItems ?? (prev.discountCode === initialData.discountCode ? prev.discountableItems : []),
      discountError: null,
      deliveryPrice: initialData.deliveryPrice,
      cartShare: initialData.cartShare,
      // Don't reset isLoading or updatingItemId during sync
    }));
    setDiscountInfo(initialData.discountInfo);
  }, [initialData]);

  const subtotal = useMemo(() => {
    return state.items.reduce((sum, item) => sum + item.total, 0);
  }, [state.items]);

  // Whether the user has free delivery regardless of cart (subscription, flag, etc.)
  const hasFreeDelivery = state.deliveryPrice === 0;

  // Delivery price comes from backend calculation (already accounts for discounts)
  const deliveryPrice = state.deliveryPrice;

  const subscriptionDiscountAmount = initialData.subscription?.discount_amount ?? 0;
  const replacementCreditDiscount = initialData.replacementCreditDiscount ?? 0;

  // Subscription discount and coupon are not cumulative — use the higher one
  const effectiveDiscount = useMemo(() => {
    if (subscriptionDiscountAmount > 0 && state.discountAmount > 0) {
      return Math.max(subscriptionDiscountAmount, state.discountAmount);
    }
    return subscriptionDiscountAmount + state.discountAmount;
  }, [subscriptionDiscountAmount, state.discountAmount]);

  const total = useMemo(() => {
    // Cap replacement credit at subtotal minus other discounts
    const afterDiscounts = subtotal - effectiveDiscount;
    const effectiveCredit = Math.min(replacementCreditDiscount, Math.max(0, afterDiscounts));
    let amount = subtotal - effectiveDiscount - effectiveCredit + deliveryPrice;
    return Math.max(amount, deliveryPrice);
  }, [subtotal, effectiveDiscount, replacementCreditDiscount, deliveryPrice]);

  const itemCount = useMemo(() => {
    return state.items.reduce((sum, item) => sum + item.quantity, 0);
  }, [state.items]);

  const hasUnfulfilledMinimum = useMemo(() => {
    // With shipping tiers, the minimum is the first tier's min_subtotal
    if (shippingTiers && shippingTiers.length > 0) {
      const firstTierMin = +[...shippingTiers].sort((a, b) => +a.min_subtotal - +b.min_subtotal)[0].min_subtotal;
      return firstTierMin > 0 && subtotal < firstTierMin;
    }
    return state.minOrder > 0 && subtotal < state.minOrder;
  }, [state.minOrder, subtotal, shippingTiers]);

  const getItemUniqueId = useCallback((id: number, formatId: number) => {
    return `${id}:${formatId}`;
  }, []);

  const updateItemOnServer = useCallback(async (id: number, formatId: number, quantity: number) => {
    const response = await fetch(route('api.cart.add_item'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCSRFToken(),
        'X-Requested-With': 'XMLHttpRequest',
        'X-Tab-Id': getTabId(),
      },
      credentials: 'include',
      body: JSON.stringify({ id, format_id: formatId, quantity }),
    });

    if (!response.ok) {
      throw new Error('Failed to update cart');
    }

    const data = await response.json();
    // Dispatch badge count from response summary
    if (data.summary?.count != null) {
      dispatchCartCount(data.summary.count);
    }
    return data.cart_share;
  }, []);

  const removeItem = useCallback(async (id: number, formatId: number) => {
    const uniqueId = getItemUniqueId(id, formatId);

    setState(prev => ({ ...prev, updatingItemId: uniqueId }));

    try {
      const cartShare = await updateItemOnServer(id, formatId, 0);

      // Recalculate discount if there's an active discount code
      const updatedItems = state.items.filter(item => !(item.id === id && item.format_id === formatId));
      if (state.discountCode && updatedItems.length > 0) {
        const discountResponse = await fetch(`${route('api.cart.get_discount')}?code=${encodeURIComponent(state.discountCode)}`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'include',
        });

        if (discountResponse.ok) {
          const discountData = await discountResponse.json();
          setState(prev => ({
            ...prev,
            items: updatedItems,
            cartShare,
            updatingItemId: null,
            discountAmount: discountData.discount ?? 0,
            discountPercentage: discountData.discount_percentage ?? null,
            discountableItems: discountData.discountable_items ?? [],
          }));
          // Refresh shipping tiers and delivery price from server
          if (onRefresh) {
            await onRefresh();
          }
          return;
        }
      }

      setState(prev => ({
        ...prev,
        items: updatedItems,
        cartShare,
        updatingItemId: null,
      }));
      // Refresh shipping tiers and delivery price from server
      if (onRefresh) {
        await onRefresh();
      }

    } catch (error) {
      console.error('Error removing item:', error);
      setState(prev => ({ ...prev, updatingItemId: null }));
    }
  }, [getItemUniqueId, updateItemOnServer, state.discountCode, state.items, onRefresh]);

  /**
   * Debounced sync with server for pending quantity changes.
   * Batches all pending changes and syncs once after debounce period.
   */
  const syncWithServer = useCallback(async () => {
    const pending = pendingChangesRef.current;
    if (pending.size === 0) return;

    // Get the first pending change (we process one at a time for simplicity)
    const [firstKey, quantity] = pending.entries().next().value as [string, number];
    const [idStr, formatIdStr] = firstKey.split(':');
    const id = parseInt(idStr, 10);
    const formatId = parseInt(formatIdStr, 10);

    // Mark as syncing (for visual indicator)
    syncingItemsRef.current.add(firstKey);
    setState(prev => ({ ...prev, updatingItemId: firstKey }));

    try {
      const cartShare = await updateItemOnServer(id, formatId, quantity);

      // Recalculate discount if there's an active discount code
      let discountUpdate = {};
      if (state.discountCode) {
        const discountResponse = await fetch(`${route('api.cart.get_discount')}?code=${encodeURIComponent(state.discountCode)}`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'include',
        });

        if (discountResponse.ok) {
          const discountData = await discountResponse.json();
          discountUpdate = {
            discountAmount: discountData.discount ?? 0,
            discountPercentage: discountData.discount_percentage ?? null,
            discountableItems: discountData.discountable_items ?? [],
          };
        }
      }

      // Remove from pending and syncing
      pending.delete(firstKey);
      syncingItemsRef.current.delete(firstKey);

      setState(prev => ({
        ...prev,
        cartShare,
        updatingItemId: null,
        ...discountUpdate,
      }));

      // Refresh shipping tiers and delivery price from server
      if (onRefresh) {
        await onRefresh();
      }

      // If there are more pending changes, schedule another sync
      if (pending.size > 0) {
        syncTimeoutRef.current = setTimeout(syncWithServer, SYNC_DEBOUNCE_MS);
      }

    } catch (error) {
      console.error('Error syncing cart:', error);
      syncingItemsRef.current.delete(firstKey);
      setState(prev => ({ ...prev, updatingItemId: null }));
      // Keep in pending to retry? For now just clear it
      pending.delete(firstKey);
    }
  }, [updateItemOnServer, state.discountCode, onRefresh]);

  /**
   * Update quantity with optimistic updates and debounced server sync.
   * Allows rapid clicking without blocking the UI.
   */
  const updateQuantity = useCallback((id: number, formatId: number, quantity: number) => {
    if (quantity < 1) return;

    const uniqueId = getItemUniqueId(id, formatId);

    // Update local state immediately (optimistic)
    setState(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === id && item.format_id === formatId) {
          return {
            ...item,
            quantity,
            total: Math.ceil(item.price * quantity * 100) / 100,
          };
        }
        return item;
      }),
    }));

    // Track pending change
    pendingChangesRef.current.set(uniqueId, quantity);

    // Clear existing timeout and schedule new sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      syncWithServer();
    }, SYNC_DEBOUNCE_MS);
  }, [getItemUniqueId, syncWithServer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Preview a discount code without applying it to session.
   * Returns the discount info if valid, sets error state if invalid.
   */
  const previewDiscount = useCallback(async (code: string) => {
    if (!code.trim()) {
      setPreviewData(null);
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, discountError: null }));

    try {
      const response = await fetch(`${route('api.cart.get_discount')}?code=${encodeURIComponent(code)}&preview=1`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Código inválido');
      }

      const data = await response.json();

      setPreviewData({
        code,
        discount: data.discount,
        discountPercentage: data.discount_percentage,
        discountableItems: data.discountable_items ?? [],
        discountInfo: data.discount_info,
      });

      setState(prev => ({ ...prev, isLoading: false, discountError: null }));
    } catch (error) {
      trackDiscountFailed(code);
      setPreviewData(null);
      setState(prev => ({
        ...prev,
        discountError: error instanceof Error ? error.message : 'Código inválido',
        isLoading: false,
      }));
    }
  }, []);

  /**
   * Confirm and apply the previewed discount to session.
   */
  const confirmDiscount = useCallback(async () => {
    if (!previewData) return;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await fetch(`${route('api.cart.get_discount')}?code=${encodeURIComponent(previewData.code)}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to apply discount');
      }

      const data = await response.json();

      if (data.discount > 0) {
        trackDiscountApplied({
          coupon_code: previewData.code,
          discount_value: data.discount,
        });
      }

      setState(prev => ({
        ...prev,
        discountCode: previewData.code,
        discountAmount: data.discount,
        discountPercentage: data.discount_percentage ?? null,
        discountableItems: data.discountable_items ?? [],
        discountError: null,
        isLoading: false,
      }));
      setDiscountInfo(data.discount_info ?? null);
      setPreviewData(null);

      // Refresh cart so the delivery price reflects the new (lower) subtotal,
      // since tiered shipping depends on the post-discount subtotal.
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [previewData, onRefresh]);

  /**
   * Cancel the discount preview without applying.
   */
  const cancelPreview = useCallback(() => {
    setPreviewData(null);
  }, []);

  /**
   * Apply a discount directly (used for initial code from URL).
   */
  const applyDiscount = useCallback(async (code: string) => {
    if (!code.trim()) {
      // Clear discount from server session
      try {
        await fetch(route('api.cart.clear_discount'), {
          method: 'DELETE',
          headers: {
            'X-XSRF-TOKEN': getCSRFToken(),
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'include',
        });
      } catch (error) {
        console.error('Error clearing discount:', error);
      }

      setState(prev => ({
        ...prev,
        discountCode: null,
        discountAmount: 0,
        discountPercentage: null,
        discountableItems: [],
        discountError: null,
      }));
      setDiscountInfo(null);

      // Refresh so the delivery price reflects the cart without the discount.
      if (onRefresh) {
        await onRefresh();
      }
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, discountError: null }));

    try {
      const response = await fetch(`${route('api.cart.get_discount')}?code=${encodeURIComponent(code)}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Código inválido');
      }

      const data = await response.json();

      if (data.discount > 0) {
        trackDiscountApplied({
          coupon_code: code,
          discount_value: data.discount,
        });
      }

      setState(prev => ({
        ...prev,
        discountCode: code,
        discountAmount: data.discount,
        discountPercentage: data.discount_percentage ?? null,
        discountableItems: data.discountable_items ?? [],
        discountError: null,
        isLoading: false,
      }));
      setDiscountInfo(data.discount_info ?? null);

      // Refresh cart so the delivery price reflects the post-discount subtotal.
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      trackDiscountFailed(code);

      setState(prev => ({
        ...prev,
        discountCode: null,
        discountAmount: 0,
        discountPercentage: null,
        discountableItems: [],
        discountError: error instanceof Error ? error.message : 'Código inválido',
        isLoading: false,
      }));
      setDiscountInfo(null);
    }
  }, [onRefresh]);

  const shareCart = useCallback(async () => {
    if (!state.cartShare) return null;

    try {
      await fetch(route('api.cart.share'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        body: JSON.stringify({ cart_share: state.cartShare }),
      });
    } catch (error) {
      console.error('Error marking cart as shared:', error);
    }

    return `${window.location.origin}/carrito?c=${state.cartShare}`;
  }, [state.cartShare]);

  const shareLink = useMemo(() => {
    if (!state.cartShare) return null;
    return `${window.location.origin}/carrito?c=${state.cartShare}`;
  }, [state.cartShare]);

  const clearDiscount = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      await fetch(route('api.cart.clear_discount'), {
        method: 'DELETE',
        headers: {
          'X-XSRF-TOKEN': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error clearing discount:', error);
    }

    setState(prev => ({
      ...prev,
      discountCode: null,
      discountAmount: 0,
      discountPercentage: null,
      discountableItems: [],
      discountError: null,
      isLoading: false,
    }));
    setDiscountInfo(null);

    // Refresh so the delivery price reflects the cart without the discount.
    if (onRefresh) {
      await onRefresh();
    }
  }, [onRefresh]);

  const removeItemsBySource = useCallback(async (source: string) => {
    const toRemove = state.items.filter(item => item.source === source);
    if (toRemove.length === 0) return;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await fetch(route('api.cart.add_item'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest',
          'X-Tab-Id': getTabId(),
        },
        credentials: 'include',
        body: JSON.stringify({
          items: toRemove.map(item => ({
            id: item.id,
            format_id: item.format_id,
            quantity: 0,
          })),
        }),
      });

      if (!response.ok) throw new Error('Failed to remove items');

      const data = await response.json();
      if (data.summary?.count != null) {
        dispatchCartCount(data.summary.count);
      }

      setState(prev => ({
        ...prev,
        items: prev.items.filter(item => item.source !== source),
        cartShare: data.cart_share ?? prev.cartShare,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Error removing items by source:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [state.items]);

  const isEmpty = useMemo(() => state.items.length === 0, [state.items]);

  return {
    state,
    items: state.items,
    minOrder: state.minOrder,
    discountCode: state.discountCode,
    discountAmount: state.discountAmount,
    discountPercentage: state.discountPercentage,
    discountableItems: state.discountableItems,
    discountError: state.discountError,
    discountInfo,
    deliveryPrice,
    cartShare: state.cartShare,
    isLoading: state.isLoading,
    updatingItemId: state.updatingItemId,
    subscription: initialData.subscription,
    replacementCreditDiscount,
    replacementCredits: initialData.replacementCredits ?? [],
    subtotal,
    total,
    itemCount,
    shareLink,
    hasUnfulfilledMinimum,
    isEmpty,
    removeItem,
    updateQuantity,
    applyDiscount,
    previewDiscount,
    confirmDiscount,
    cancelPreview,
    previewData,
    clearDiscount,
    shareCart,
    getItemUniqueId,
    removeItemsBySource,
    shippingTiers,
    currentShippingTier,
    nextShippingTier,
    hasFreeDelivery,
  };
}
